"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { CheckCircle2, ChevronRight, KeyRound, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type AccountOption = {
  setup_id?: string | null;
  workspace_type?: string | null;
  workspace_key?: string | null;
  resource_key?: string | null;
  connection_key?: string | null;
  display_name?: string | null;
  provider_key?: string | null;
  resource_type?: string | null;
  account_ref?: string | null;
  project_ref?: string | null;
  target_account_ref?: string | null;
  target_member_email?: string | null;
  target_project_ref?: string | null;
  backend_stage?: string | null;
  lifecycle_state?: string | null;
  health_state?: string | null;
  setup_state?: string | null;
  primary_action?: string | null;
  selectable?: boolean;
};

type CredentialField = {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
};

type AuthenticationMethod = {
  binding_key: string;
  choice_key: string;
  display_name: string;
  auth_method: string;
  state: string;
  selectable: boolean;
  reason?: string | null;
  phase?: string;
  execution_route?: string;
  adapter_key?: string | null;
  interaction_adapter_key?: string | null;
  credential_schema?: { fields?: CredentialField[] };
  procedure?: {
    procedure_key?: string | null;
    version?: string | null;
    steps?: Array<Record<string, unknown>>;
  };
};

type ScreenPayload = {
  screen?: string;
  title?: string;
  state?: string;
  current_account?: {
    setup_id?: string;
    display_name?: string | null;
    provider_key?: string | null;
    current_account?: string | null;
    target_account_ref?: string | null;
    target_member_email?: string | null;
    target_project_ref?: string | null;
    lifecycle_state?: string | null;
    backend_stage?: string | null;
    status_summary?: string | null;
    action_kind?: string | null;
    selected_authentication_binding_key?: string | null;
    selected_authentication_method?: string | null;
  };
  available_authentication_methods?: AuthenticationMethod[];
  selected_authentication_method?: AuthenticationMethod | null;
  options?: AccountOption[];
};

const VISUAL_STEPS = ["Choose Method", "Prepare", "Authenticate", "Verify", "Ready"] as const;

function stateTone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["READY", "ACTIVE", "CONNECTED", "VERIFIED", "AVAILABLE"].includes(state)) return "bg-emerald-400/10 text-emerald-600 dark:text-emerald-300";
  if (["ACTION_REQUIRED", "AUTH_REQUIRED", "CREDENTIAL_REQUIRED", "NOT_CURRENT_STAGE", "READINESS_CHECK_REQUIRED"].includes(state)) return "bg-amber-400/10 text-amber-700 dark:text-amber-200";
  if (["FAILED", "DISABLED", "DISABLED_BY_CONFIGURATION", "ADAPTER_UNAVAILABLE", "ADAPTER_REQUIRED"].includes(state)) return "bg-red-400/10 text-red-600 dark:text-red-300";
  return "bg-foreground/[0.055] text-foreground/58";
}

function procedureStepLabel(step: Record<string, unknown>, index: number) {
  return String(step.label ?? step.title ?? step.name ?? step.action ?? `Step ${index + 1}`);
}

export function UniversalAccountsSetupPanel({
  supabase,
  connected,
  setupId,
  providerKey,
  targetAccountRef,
  compact = false,
  onComplete,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  setupId?: string | null;
  providerKey?: string | null;
  targetAccountRef?: string | null;
  compact?: boolean;
  onComplete?: () => void;
}) {
  const [activeSetupId, setActiveSetupId] = useState<string | null>(setupId ?? null);
  const [directory, setDirectory] = useState<AccountOption[]>([]);
  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const [selectedWorkspace, setSelectedWorkspace] = useState<AccountOption | null>(null);
  const [restoreCurrentWorkspace, setRestoreCurrentWorkspace] = useState(true);
  const [screen, setScreen] = useState<ScreenPayload | null>(null);
  const [actionResult, setActionResult] = useState<Record<string, any> | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (setupId) setActiveSetupId(setupId);
  }, [setupId]);

  const contextBody = useCallback(() => ({
    ...(activeSetupId ? { setup_id: activeSetupId } : {}),
    ...(!activeSetupId && providerKey ? { provider_key: providerKey } : {}),
    ...(!activeSetupId && targetAccountRef ? { target_account_ref: targetAccountRef } : {}),
  }), [activeSetupId, providerKey, targetAccountRef]);

  const invokeAccounts = useCallback(async (action: string, extra: Record<string, unknown> = {}) => {
    if (!supabase || !connected) throw new Error("RUNYI session is not connected.");
    const { data, error: invokeError } = await supabase.functions.invoke("runyi-accounts-setup", {
      body: { action, ...contextBody(), ...extra },
    });
    if (invokeError) throw invokeError;
    if (data?.error) throw new Error(String(data.detail ?? data.error));
    return data as Record<string, any>;
  }, [connected, contextBody, supabase]);

  const loadDirectory = useCallback(async () => {
    if (!supabase || !connected) return;
    setBusy("directory");
    setError(null);
    try {
      const { data, error: searchError } = await supabase.rpc("runyi_service_infrastructure_workspace_search_current", {
        p_query: workspaceQuery.trim(), p_workspace_type: "ALL", p_limit: 40, p_offset: 0,
      });
      if (searchError) throw searchError;
      const rows = Array.isArray((data as any)?.items) ? (data as any).items : [];
      const mapped: AccountOption[] = rows.map((item: any) => ({
        setup_id: item.setup_id ? String(item.setup_id) : null,
        workspace_type: item.workspace_type ?? null, workspace_key: item.workspace_key ?? null,
        resource_key: item.resource_key ?? null, connection_key: item.connection_key ?? null,
        display_name: item.display_name ?? null, provider_key: item.provider_key ?? null, resource_type: item.resource_type ?? null,
        account_ref: item.account_ref ?? null, project_ref: item.project_ref ?? null,
        target_account_ref: item.account_ref ?? null, target_member_email: null, target_project_ref: item.project_ref ?? null,
        backend_stage: item.setup_state ?? null, lifecycle_state: item.lifecycle_state ?? null,
        health_state: item.health_state ?? null, setup_state: item.setup_state ?? null,
        primary_action: item.primary_action ?? null, selectable: item.selectable !== false,
      }));
      setDirectory(mapped);
      if (restoreCurrentWorkspace && !workspaceQuery.trim() && !activeSetupId && !selectedWorkspace) {
        const { data: current, error: currentError } = await supabase.rpc("runyi_service_get_infrastructure_workspace_current");
        if (currentError) throw currentError;
        const context = (current as any)?.active_context;
        const currentType = String(context?.workspace_type ?? "").toUpperCase();
        if (currentType === "ACCOUNT_SETUP" && context?.setup_id) { setActiveSetupId(String(context.setup_id)); return; }
        if (currentType === "REGISTERED_INFRASTRUCTURE") {
          const currentKey = String(context?.workspace_key ?? "");
          const match = mapped.find((item) => item.workspace_key === currentKey)
            ?? mapped.find((item) => item.resource_key && item.resource_key === context?.resource_key)
            ?? mapped.find((item) => item.connection_key && item.connection_key === context?.connection_key);
          if (match) setSelectedWorkspace(match);
        }
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "RUNYI could not load infrastructure workspaces.");
    } finally { setBusy(null); }
  }, [activeSetupId, connected, restoreCurrentWorkspace, selectedWorkspace, supabase, workspaceQuery]);

  const selectWorkspace = useCallback(async (item: AccountOption) => {
    if (!supabase || !connected || item.selectable === false) return;
    const workspaceType = String(item.workspace_type ?? (item.setup_id ? "ACCOUNT_SETUP" : "REGISTERED_INFRASTRUCTURE")).toUpperCase();
    const workspaceKey = String(item.workspace_key ?? (item.setup_id ? `SETUP:${item.setup_id}` : item.resource_key ? `RESOURCE:${item.resource_key}` : ""));
    if (!workspaceKey) return;
    setBusy(`workspace:${workspaceKey}`); setError(null);
    try {
      const { error: setErrorValue } = await supabase.rpc("runyi_service_set_infrastructure_workspace_current", {
        p_workspace_type: workspaceType, p_workspace_key: workspaceKey,
        p_resource_key: workspaceType === "REGISTERED_INFRASTRUCTURE" ? item.resource_key ?? null : null,
        p_connection_key: workspaceType === "REGISTERED_INFRASTRUCTURE" ? item.connection_key ?? null : null,
        p_setup_id: workspaceType === "ACCOUNT_SETUP" ? item.setup_id ?? null : null,
        p_member_scope: "ALL_MEMBERS", p_application_scope: null,
      });
      if (setErrorValue) throw setErrorValue;
      setRestoreCurrentWorkspace(true);
      if (workspaceType === "ACCOUNT_SETUP" && item.setup_id) {
        setSelectedWorkspace(null); setScreen(null); setActiveSetupId(item.setup_id);
      } else {
        setActiveSetupId(null); setScreen(null); setSelectedWorkspace(item);
        setMessage(`${item.display_name ?? item.provider_key ?? "Infrastructure"} workspace selected.`);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "RUNYI could not switch infrastructure workspace.");
    } finally { setBusy(null); }
  }, [connected, supabase]);

  const loadScreen = useCallback(async () => {
    if (!supabase || !connected) return;
    setBusy("screen"); setError(null);
    try {
      const data = await invokeAccounts("screen"); setScreen(data as ScreenPayload);
      if (data?.state === "ACCOUNT_SELECTION_REQUIRED" && Array.isArray(data.options)) {
        setDirectory(data.options.map((item: any) => ({ ...item,
          setup_id: item.setup_id ? String(item.setup_id) : null, workspace_type: "ACCOUNT_SETUP",
          workspace_key: item.setup_id ? `SETUP:${item.setup_id}` : null,
          account_ref: item.target_account_ref ?? item.target_member_email ?? null,
          project_ref: item.target_project_ref ?? null, setup_state: item.backend_stage ?? null, selectable: true,
        })));
      }
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : "Universal Accounts Setup could not load.";
      if (/ACCOUNTS_SETUP_CONTEXT_NOT_FOUND|SETUP_ID_OR_PROVIDER_REQUIRED/.test(detail)) {
        setActiveSetupId(null); setSelectedWorkspace(null); setScreen(null); setRestoreCurrentWorkspace(false);
        await loadDirectory(); return;
      }
      setError(detail);
    } finally { setBusy(null); }
  }, [connected, invokeAccounts, loadDirectory, supabase]);

  useEffect(() => {
    setActionResult(null);
    setCredentialValues({});
    setMessage(null);
    setError(null);
    if (!connected || !supabase) return;
    if (activeSetupId || providerKey) void loadScreen();
    else void loadDirectory();
  }, [activeSetupId, connected, loadDirectory, loadScreen, providerKey, supabase]);

  const selected = screen?.selected_authentication_method ?? null;
  const account = screen?.current_account ?? null;
  const backendStage = String(account?.backend_stage ?? "").toUpperCase();
  const accountReady = ["CONNECTED", "VERIFIED", "MARKETPLACE_READY", "READY"].includes(backendStage);
  const resultState = String(actionResult?.state ?? "").toUpperCase();
  const visualStep = accountReady ? 5
    : ["VERIFICATION_RESULT", "VERIFICATION_READY", "CREDENTIAL_BOUND"].includes(resultState) ? 4
      : resultState === "AUTHENTICATION_IN_PROGRESS" ? 3
        : selected ? 2 : 1;

  const credentialSchema = useMemo(() => {
    const explicit = actionResult?.credential_schema?.fields;
    if (Array.isArray(explicit)) return explicit as CredentialField[];
    return selected?.credential_schema?.fields ?? [];
  }, [actionResult, selected]);

  const launchAuthorization = useCallback((url: string) => {
    if (typeof window === "undefined") return;
    type BrowserBridge = {
      openOneTimeInfrastructureBrowser?: (value: string) => boolean;
      openOneTimeOAuth?: (value: string) => boolean;
    };
    const bridgeWindow = window as typeof window & { RunyiAndroid?: BrowserBridge; RUNYINative?: BrowserBridge };
    const nativeBridge = bridgeWindow.RunyiAndroid ?? bridgeWindow.RUNYINative;
    const openNative = nativeBridge?.openOneTimeInfrastructureBrowser ?? nativeBridge?.openOneTimeOAuth;
    if (nativeBridge && typeof openNative === "function") {
      if (openNative.call(nativeBridge, url) === true) return;
    }
    window.location.assign(url);
  }, []);

  async function chooseMethod(method: AuthenticationMethod) {
    if (!method.selectable) return;
    setBusy(`select:${method.binding_key}`);
    setError(null);
    setMessage(null);
    setActionResult(null);
    setCredentialValues({});
    try {
      const data = await invokeAccounts("select_method", { binding_key: method.binding_key });
      setMessage(`${method.display_name} selected. RUNYI loaded the backend procedure for this account.`);
      setScreen((current) => current ? { ...current, state: "METHOD_SELECTED", selected_authentication_method: data.selected_authentication_method ?? method } : current);
      await loadScreen();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "RUNYI could not select this authentication method.");
    } finally {
      setBusy(null);
    }
  }

  async function beginAuthentication() {
    if (!selected) return;
    setBusy("begin");
    setError(null);
    setMessage(null);
    try {
      const data = await invokeAccounts("begin_authentication");
      setActionResult(data);
      const authorizationUrl = String(data?.result?.authorization_url ?? data?.authorization_url ?? "");
      if (authorizationUrl) {
        setMessage("Authentication was prepared by RUNYI. Opening the governed authorization surface now.");
        launchAuthorization(authorizationUrl);
        return;
      }
      if (String(data?.state ?? "").toUpperCase() === "CREDENTIAL_INPUT_REQUIRED") {
        setMessage("RUNYI resolved secure credential input for the selected method.");
      } else {
        setMessage("The selected authentication procedure is ready for verification.");
      }
      await loadScreen();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Authentication could not be prepared.");
    } finally {
      setBusy(null);
    }
  }

  async function submitCredentials() {
    if (!selected || credentialSchema.length === 0) return;
    const credentials = { ...credentialValues };
    const missing = credentialSchema.filter((field) => field.required && !String(credentials[field.key] ?? "").trim());
    if (missing.length) {
      setError(`Complete ${missing.map((field) => field.label ?? field.key).join(", ")}.`);
      return;
    }
    setCredentialValues({});
    setBusy("credentials");
    setError(null);
    try {
      const data = await invokeAccounts("submit_credentials", { credentials });
      setActionResult(data);
      setMessage("Credentials were submitted to RUNYI's protected backend and cleared from this form.");
      await loadScreen();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Credential binding failed.");
    } finally {
      Object.keys(credentials).forEach((key) => { credentials[key] = ""; });
      setBusy(null);
    }
  }

  async function verifyAccess() {
    setBusy("verify");
    setError(null);
    try {
      const data = await invokeAccounts("verify_access");
      setActionResult(data);
      setMessage("RUNYI completed the provider verification request.");
      await loadScreen();
      onComplete?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Provider access verification failed.");
    } finally {
      setBusy(null);
    }
  }

  if (!connected || !supabase) {
    return <div className="rounded-2xl border border-foreground/8 bg-card p-4 text-xs text-foreground/50">Sign in to RUNYI to manage account authentication.</div>;
  }

  if ((!activeSetupId && !providerKey) || screen?.state === "ACCOUNT_SELECTION_REQUIRED") {
    const selectedState = selectedWorkspace?.health_state || selectedWorkspace?.setup_state || selectedWorkspace?.backend_stage || selectedWorkspace?.lifecycle_state || "WORKSPACE";
    return (
      <section className={`rounded-2xl border border-foreground/8 bg-card ${compact ? "p-3" : "p-4"}`}>
        <div className="flex items-center justify-between gap-3">
          <div><p className="text-sm font-semibold">Infrastructure Workspace</p><p className="mt-1 text-[10px] text-foreground/45">Search registered infrastructure, switch workspace, or continue a new account setup.</p></div>
          <Button type="button" variant="outline" size="icon" className="size-8 rounded-xl" onClick={() => void loadDirectory()} disabled={busy === "directory"}><RefreshCw className={`size-3.5 ${busy === "directory" ? "animate-spin" : ""}`} /></Button>
        </div>
        <Input value={workspaceQuery} onChange={(event) => setWorkspaceQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void loadDirectory(); }} placeholder="Search infrastructure, provider, account or project" className="mt-3" autoComplete="off" />
        {selectedWorkspace ? <div className="mt-3 rounded-xl border border-primary/20 bg-primary/[0.045] p-3"><div className="flex items-center gap-2"><span className="min-w-0 flex-1"><span className="block truncate text-[11px] font-semibold">Current workspace · {selectedWorkspace.display_name || selectedWorkspace.resource_key || "Infrastructure"}</span><span className="mt-0.5 block truncate text-[9px] text-foreground/45">{selectedWorkspace.provider_key || "Provider"} · {selectedWorkspace.account_ref || selectedWorkspace.project_ref || selectedWorkspace.resource_type || "Registered infrastructure"}</span></span><Badge className={stateTone(selectedState)}>{selectedState}</Badge></div></div> : null}
        {error && <p className="mt-3 rounded-xl bg-red-400/[0.07] p-2.5 text-[10px] text-red-600 dark:text-red-300">{error}</p>}
        <div className="mt-3 space-y-1.5">
          {busy === "directory" && directory.length === 0 ? <div className="grid min-h-24 place-items-center"><LoaderCircle className="size-5 animate-spin text-primary" /></div> : null}
          {directory.map((item) => {
            const workspaceType = String(item.workspace_type ?? (item.setup_id ? "ACCOUNT_SETUP" : "REGISTERED_INFRASTRUCTURE")).toUpperCase();
            const itemState = item.health_state || item.setup_state || item.backend_stage || item.lifecycle_state || "WORKSPACE";
            const active = Boolean(selectedWorkspace?.workspace_key && selectedWorkspace.workspace_key === item.workspace_key);
            return <button type="button" key={item.workspace_key || item.setup_id || item.resource_key || item.connection_key || item.display_name || "workspace"} onClick={() => void selectWorkspace(item)} disabled={item.selectable === false || busy !== null} className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition ${active ? "border-primary/30 bg-primary/[0.05]" : "border-foreground/8 bg-foreground/[0.018] hover:bg-foreground/[0.04]"}`}>
              <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary"><ShieldCheck className="size-3.5" /></span>
              <span className="min-w-0 flex-1"><span className="block truncate text-[11px] font-semibold">{item.display_name || `${item.provider_key ?? "Provider"} infrastructure`}</span><span className="mt-0.5 block truncate text-[9px] text-foreground/42">{workspaceType === "ACCOUNT_SETUP" ? "Account setup" : item.resource_type || "Registered infrastructure"} · {item.account_ref || item.target_account_ref || item.target_member_email || item.project_ref || "Identity pending"}</span></span>
              <Badge className={stateTone(itemState)}>{itemState}</Badge><ChevronRight className="size-3.5 text-foreground/30" />
            </button>;
          })}
          {busy !== "directory" && directory.length === 0 && !error ? <p className="py-6 text-center text-[10px] text-foreground/42">No matching infrastructure or account setup is currently registered.</p> : null}
        </div>
      </section>
    );
  }

  if (!screen && busy === "screen") return <div className="grid min-h-40 place-items-center rounded-2xl border border-foreground/8 bg-card"><LoaderCircle className="size-5 animate-spin text-primary" /></div>;

  const methods = screen?.available_authentication_methods ?? [];
  return (
    <section className={`rounded-2xl border border-foreground/8 bg-card ${compact ? "p-3" : "p-4"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">Universal Accounts Setup</p><h3 className="mt-1 truncate text-base font-semibold">{account?.display_name || account?.current_account || "Account Setup"}</h3><p className="mt-1 truncate text-[10px] text-foreground/45">{account?.provider_key || providerKey || "Provider"} · {account?.target_account_ref || account?.target_member_email || "Identity pending"}</p></div>
        <div className="flex shrink-0 gap-1"><Badge className={stateTone(backendStage)}>{backendStage || screen?.state || "SETUP"}</Badge><Button type="button" variant="outline" size="icon" className="size-8 rounded-xl" onClick={() => void loadScreen()} disabled={busy === "screen"}><RefreshCw className={`size-3.5 ${busy === "screen" ? "animate-spin" : ""}`} /></Button></div>
      </div>

      {!setupId && activeSetupId ? <button type="button" className="mt-2 text-[9px] font-semibold text-primary" onClick={() => { setRestoreCurrentWorkspace(false); setActiveSetupId(null); setSelectedWorkspace(null); setScreen(null); }}>← Infrastructure workspace</button> : null}

      <div className="mt-4 grid grid-cols-5 gap-1">
        {VISUAL_STEPS.map((label, index) => {
          const number = index + 1;
          const done = number < visualStep || accountReady;
          const active = number === visualStep && !accountReady;
          return <div key={label} className={`rounded-lg px-1.5 py-2 text-center ${done ? "bg-emerald-400/[0.08]" : active ? "bg-primary/[0.08]" : "bg-foreground/[0.025]"}`}><div className={`mx-auto grid size-5 place-items-center rounded-full text-[9px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-primary text-primary-foreground" : "bg-foreground/8 text-foreground/40"}`}>{done ? <CheckCircle2 className="size-3" /> : number}</div><p className="mt-1 truncate text-[8px] text-foreground/50">{label}</p></div>;
        })}
      </div>

      {account?.status_summary ? <p className="mt-3 rounded-xl bg-foreground/[0.025] p-2.5 text-[10px] leading-4 text-foreground/52">{account.status_summary}</p> : null}
      {message ? <p className="mt-3 rounded-xl bg-primary/[0.055] p-2.5 text-[10px] leading-4 text-foreground/62">{message}</p> : null}
      {error ? <p className="mt-3 rounded-xl bg-red-400/[0.07] p-2.5 text-[10px] leading-4 text-red-600 dark:text-red-300">{error}</p> : null}

      {accountReady ? (
        <div className="mt-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.05] p-4 text-center"><CheckCircle2 className="mx-auto size-6 text-emerald-500" /><p className="mt-2 text-sm font-semibold">Authentication ready</p><p className="mt-1 text-[10px] text-foreground/48">RUNYI has a verified provider connection for this account. Infrastructure setup can continue.</p></div>
      ) : (
        <>
          <div className="mt-4"><p className="text-[10px] font-semibold">Step 1 · Choose an authentication method</p><div className="mt-2 space-y-1.5">
            {methods.map((method) => {
              const isSelected = selected?.binding_key === method.binding_key;
              return <button type="button" key={method.binding_key} disabled={!method.selectable || busy !== null} onClick={() => void chooseMethod(method)} className={`w-full rounded-xl border px-3 py-2.5 text-left transition ${isSelected ? "border-primary/30 bg-primary/[0.055]" : "border-foreground/8 bg-foreground/[0.015]"} ${method.selectable ? "hover:bg-foreground/[0.04]" : "opacity-60"}`}><div className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{method.display_name}</span><Badge className={stateTone(method.state)}>{method.state}</Badge></div>{method.reason ? <p className="mt-1 text-[9px] leading-4 text-foreground/42">{method.reason}</p> : null}</button>;
            })}
            {methods.length === 0 && busy !== "screen" ? <p className="py-4 text-center text-[10px] text-foreground/42">RUNYI has not resolved an authentication method for this account yet.</p> : null}
          </div></div>

          {selected ? <div className="mt-4 rounded-xl border border-foreground/8 bg-foreground/[0.018] p-3"><div className="flex items-center gap-2"><KeyRound className="size-4 text-primary" /><div className="min-w-0 flex-1"><p className="truncate text-[11px] font-semibold">{selected.display_name}</p><p className="text-[9px] text-foreground/40">{selected.procedure?.procedure_key || selected.execution_route || "Backend procedure"}</p></div><button type="button" className="text-[9px] font-semibold text-primary" onClick={() => setActionResult(null)}>Change</button></div>{Array.isArray(selected.procedure?.steps) && selected.procedure!.steps!.length > 0 ? <div className="mt-3 space-y-1">{selected.procedure!.steps!.map((step, index) => <div key={index} className="flex items-start gap-2 rounded-lg bg-background/55 px-2.5 py-2"><span className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground/[0.06] text-[8px] font-bold">{index + 1}</span><span className="text-[9px] leading-4 text-foreground/52">{procedureStepLabel(step, index)}</span></div>)}</div> : null}</div> : null}

          {resultState === "CREDENTIAL_INPUT_REQUIRED" ? <div className="mt-4 space-y-2"><p className="text-[10px] font-semibold">Secure credential input</p>{credentialSchema.map((field) => <label key={field.key} className="block"><span className="text-[9px] text-foreground/48">{field.label || field.key}{field.required ? " *" : ""}</span>{String(field.type).includes("textarea") ? <Textarea value={credentialValues[field.key] ?? ""} onChange={(event) => setCredentialValues((current) => ({ ...current, [field.key]: event.target.value }))} className="mt-1 min-h-28 font-mono text-xs" autoComplete="off" spellCheck={false} /> : <Input type={String(field.type).includes("secret") ? "password" : "text"} value={credentialValues[field.key] ?? ""} onChange={(event) => setCredentialValues((current) => ({ ...current, [field.key]: event.target.value }))} className="mt-1" autoComplete="off" spellCheck={false} />}</label>)}<Button type="button" className="w-full" onClick={() => void submitCredentials()} disabled={busy !== null}>{busy === "credentials" ? <LoaderCircle className="size-4 animate-spin" /> : <KeyRound className="size-4" />}Submit securely</Button></div> : null}

          {selected && resultState !== "CREDENTIAL_INPUT_REQUIRED" ? <div className="mt-4 grid gap-2 sm:grid-cols-2"><Button type="button" onClick={() => void beginAuthentication()} disabled={busy !== null}>{busy === "begin" ? <LoaderCircle className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}Continue authentication</Button><Button type="button" variant="outline" onClick={() => void verifyAccess()} disabled={busy !== null}>{busy === "verify" ? <LoaderCircle className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}Verify connection</Button></div> : null}
        </>
      )}
      <p className="mt-3 text-[9px] leading-4 text-foreground/35">Authentication methods, readiness, procedures and adapters are resolved by RUNYI. This screen does not define provider-specific authentication logic.</p>
    </section>
  );
}
